// src/pages/msp-inventory/components/reports/MonthEndValuationReminder.jsx
// Reminds the user to download their Inventory Valuation at month-end,
// since MSP doesn't store month-end valuations automatically (yet).
//  - Last `remindDays` days of the month: "Month-end in X days" (red on the last day)
//  - First `afterDays` days of the next month: "Did you save last month's?"
// Dismiss hides it until tomorrow (per browser).
import React, { useState } from "react";
import { CalendarClock, X } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const DISMISS_KEY = "msp_valuation_reminder_dismissed_on";

const MonthEndValuationReminder = ({
    remindDays = 5,   // show this many days before month-end
    afterDays = 3,    // and on the first N days of the next month
    actionLabel,      // optional button, e.g. "Open Valuation"
    onAction,
    hint,             // optional extra text for the "before" message
    wrapperClassName = "",
}) => {
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === dayKey(new Date()); } catch { return false; }
    });

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const daysLeft = Math.round((monthEnd - today) / 86400000);
    const isBefore = daysLeft <= remindDays;
    const isAfter = now.getDate() <= afterDays;

    if (dismissed || (!isBefore && !isAfter)) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, dayKey(new Date())); } catch { /* ignore */ }
        setDismissed(true);
    };

    const urgent = isBefore && daysLeft === 0;
    let title;
    let body;
    if (isBefore) {
        title = urgent
            ? `Today is month-end (${fmt(monthEnd)})`
            : `Month-end in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (${fmt(monthEnd)})`;
        body = `Download your Inventory Valuation late on ${fmt(monthEnd)} to keep an exact record of this month's closing inventory value. MSP doesn't save it automatically yet.${hint ? ` ${hint}` : ""}`;
    } else {
        title = `Did you save your ${fmt(prevMonthEnd)} inventory valuation?`;
        body = `If not, pick ${fmt(prevMonthEnd)} in the Inventory Valuation "Month-end" picker to see an estimate.`;
    }

    return (
        <div className={wrapperClassName}>
            <div className={`rounded-xl border px-4 py-3 flex items-start justify-between gap-3 ${urgent ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
                <div className="flex items-start gap-2 text-xs">
                    <CalendarClock size={16} className="mt-0.5 shrink-0" />
                    <div>
                        <p className="font-bold">{title}</p>
                        <p className="mt-0.5">{body}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {onAction && actionLabel && (
                        <button type="button" onClick={onAction}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 whitespace-nowrap">
                            {actionLabel}
                        </button>
                    )}
                    <button type="button" onClick={dismiss} title="Hide until tomorrow"
                        className="p-1 rounded text-gray-400 hover:text-gray-700">
                        <X size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MonthEndValuationReminder;